import InfoIcon from "@mui/icons-material/Info";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import {
  Backdrop,
  Badge,
  Box,
  Button,
  Grow,
  SpeedDial,
  SpeedDialAction,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  styled,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useRouter } from "next/router";
import { Fragment, useCallback, useContext, useMemo, useState } from "react";

import useNavActions from "@/components/menu/useNavActions";
import { PAGE_PADDING } from "@/constants/layout";
import { Z_INDEX } from "@/constants/zIndex";
import { CartDrawerContext } from "@/context/CartDrawerContext";
import useDesignType from "@/hooks/useDesignType";
import { useAppSelector } from "@/store";
import {
  selectCartCount,
  selectCartTotalDiscountApplied,
} from "@/store/slices/cartSlice";
import { formatCurrency } from "@/utils/format";
import { calculateOrderTotals } from "@/utils/pricing";

/**
 * `q` in `mod-9802.js` — the order bar, and the last piece of the
 * `web_design_type` layer.
 *
 * ```js
 * h ? <Q …mobile bottom bar: "View Order" + badge + info toggle + SpeedDial… />
 *   : v ? <b totalSelectedItems totalPrice totalSavings currency_symbol
 *            orderBarOpened setOpen />
 *       : null
 * ```
 *
 * `h = useMediaQuery(down("lg"))` and `v = web_design_type.startsWith("MODERN")`.
 * So below 900px everyone gets the bottom bar; above it **`MODERN*` gets the
 * floating "View Cart" pill and `CLASSIC*` gets NOTHING**, because under
 * `CLASSIC*` the cart is a *permanent* drawer that is already on screen. That
 * pairing is enforced in `CartPanel` (`variant: isTemporary || isModernDesign`).
 *
 * The mobile bar is **not** gated on the basket having anything in it — the
 * SpeedDial is where the navbar's chips go below `md`, so it has to stay
 * reachable with an empty basket. The badge hides its own zero.
 */

/* ------------------------- the MODERN pill (`b`) -------------------------- */

/** `v` */
const PillRoot = styled(Box)(({ theme }) => ({
  position: "fixed",
  zIndex: Z_INDEX.HIGH,
  left: "50%",
  transform: "translateX(-50%)",
  bottom: PAGE_PADDING,
  width: "calc(100% - 32px)",
  maxWidth: "300px",
  // Verbatim from the original even though the pill never renders below `lg`.
  [theme.breakpoints.down("lg")]: { bottom: 4 * PAGE_PADDING + 5 },
}));

/** `j` */
const PillButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(2, 3),
  borderRadius: theme.spacing(2),
  textTransform: "none",
  boxShadow: theme.shadows[8],
  "&:hover": { boxShadow: theme.shadows[12] },
}));

/** `y` */
const PillRow = styled(Stack)({
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  width: "100%",
  gap: "16px",
});

/** `Z` */
const PillLeft = styled(Stack)({
  flexDirection: "row",
  alignItems: "center",
  gap: "12px",
});

/** `w` */
const PillBadge = styled(Badge)(({ theme }) => ({
  "& .MuiBadge-badge": {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    border: `2px solid ${theme.palette.primary.contrastText}`,
    padding: "0 6px",
    fontSize: "0.75rem",
    fontWeight: "bold",
    minWidth: "20px",
    height: "20px",
  },
}));

/** The savings colour is a literal in the original, not a palette entry. */
const SAVINGS_COLOR = "#66BB6A";

interface ViewCartPillProps {
  totalSelectedItems: number;
  totalPrice?: number | null;
  totalSavings?: number | null;
  currencySymbol?: string;
  currencyCode?: string;
  orderBarOpened: boolean;
  setOpen: (open: boolean) => void;
}

/** `b` */
function ViewCartPill({
  totalSelectedItems,
  totalPrice,
  totalSavings,
  currencySymbol,
  currencyCode,
  orderBarOpened,
  setOpen,
}: ViewCartPillProps) {
  const money = (value: number) =>
    formatCurrency(value, { symbol: currencySymbol, code: currencyCode });

  return (
    <PillRoot>
      <Grow
        in={!orderBarOpened && totalSelectedItems > 0}
        timeout={300}
        unmountOnExit
      >
        <Box>
          <PillButton
            variant="contained"
            color="primary"
            size="large"
            onClick={() => setOpen(true)}
            fullWidth
          >
            <PillRow>
              <PillLeft>
                <PillBadge badgeContent={totalSelectedItems}>
                  <ShoppingCartIcon />
                </PillBadge>
                <Typography variant="body1" fontWeight="bold">
                  View Cart
                </Typography>
              </PillLeft>
              <Stack alignItems="flex-end">
                {totalPrice != null && (
                  <Typography variant="body2" fontWeight="bold">
                    {money(totalPrice)}
                  </Typography>
                )}
                {totalSavings != null && totalSavings > 0 && (
                  <Stack direction="row" alignItems="center" spacing={0.3}>
                    <LocalOfferIcon sx={{ fontSize: 12, color: SAVINGS_COLOR }} />
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      sx={{ color: SAVINGS_COLOR, fontSize: "0.65rem" }}
                    >
                      You save {money(totalSavings)}
                    </Typography>
                  </Stack>
                )}
              </Stack>
            </PillRow>
          </PillButton>
        </Box>
      </Grow>
    </PillRoot>
  );
}

/* ----------------------- the mobile bottom bar (`Q`) ---------------------- */

/** `Q` */
const MobileBar = styled(Stack)(({ theme }) => ({
  position: "sticky",
  bottom: 0,
  zIndex: Z_INDEX.LOW,
  width: "100%",
  padding: `10px ${PAGE_PADDING}px`,
  backgroundColor: theme.palette.background.default,
}));

/** `X` */
const MobileBadge = styled(Badge)(({ theme }) => ({
  "& .MuiBadge-badge": {
    right: -4,
    top: 5,
    border: `2px solid ${theme.palette.primary.main}`,
    padding: "0 4px",
    fontSize: 9,
  },
}));

/** `J` — anchored to the sticky bar, which is its positioned ancestor. */
const Dial = styled(SpeedDial)({ position: "absolute", bottom: 41, right: 7 });

/** `$` */
const DialBackdrop = styled(Backdrop)({ zIndex: Z_INDEX.BASE });

/**
 * `ee` — the info toggle only exists below `md` (800). Between `md` and `lg`
 * the bar therefore shows no trigger for the SpeedDial, which is correct: from
 * `md` up the navbar stops collapsing to one chip and carries them all itself.
 */
const InfoToggleGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  display: "none",
  [theme.breakpoints.down("md")]: { display: "block" },
}));

/* --------------------------------- `q` ----------------------------------- */

export default function OrderBar() {
  const theme = useTheme();
  const [dialOpen, setDialOpen] = useState(false);

  const { open: orderBarOpened, setOpen } = useContext(CartDrawerContext);
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const { isModernDesign } = useDesignType();
  const actions = useNavActions();
  const router = useRouter();

  /**
   * "VIEW ORDER" OPENS THE CHECKOUT, FOR EVERYONE.
   *
   * The cart drawer was a screen titled "Order" whose only forward action was
   * its own total button, and that button opened the checkout - which shows
   * the same lines, the same totals, and everything needed to finish. So the
   * drawer cost one tap and one screen and added nothing. It is skipped for
   * customers and staff alike, on the operator's instruction ("its an extra
   * screen").
   *
   * The drawer component stays mounted: nothing opens it now, but it is the
   * `CLASSIC*` layout's PERMANENT sidebar above 900px, which is a different
   * surface with the same component.
   */
  const openOrder = useCallback(() => {
    void router.push("/review-order");
  }, [router]);

  const totalSelectedItems = useAppSelector(selectCartCount);
  const totalSavings = useAppSelector(selectCartTotalDiscountApplied);
  const settings = useAppSelector((state) => state.settings.data);
  const { items, orderType, paymentMethod, address } = useAppSelector(
    (state) => state.cart,
  );

  // `totalPrice` is the cart's `grand_total`. This reconstruction derives it
  // with the same helper the cart drawer and the review page use, so the three
  // can never disagree.
  const totals = useMemo(
    () =>
      calculateOrderTotals({
        items,
        orderType,
        paymentMethod,
        settings,
        deliveryCost: address?.deliveryCost ?? 0,
        distanceMeters: address?.distanceInMeters,
      }),
    [items, orderType, paymentMethod, settings, address],
  );

  // `f.filter(e => !e.hide).map(…).reverse()` — an action with a submenu is
  // FLATTENED into one FAB per entry, and the whole list is reversed so the
  // first action ends up nearest the trigger.
  const dialActions = useMemo(
    () =>
      actions
        .filter((entry) => !entry.hide)
        .flatMap((entry, index) =>
          entry.menuItems
            ? entry.menuItems.map((item, itemIndex) => ({
                key: `${index}-${itemIndex}`,
                label: item.label,
                icon: item.icon ?? entry.icon,
                onClick: item.onClick,
              }))
            : [
                {
                  key: String(index),
                  label: entry.label,
                  icon: entry.icon,
                  onClick: entry.onClick,
                },
              ],
        )
        .reverse(),
    [actions],
  );

  return (
    <Fragment>
      <DialBackdrop open={dialOpen} onClick={() => setDialOpen(false)} />
      {isMobile ? (
        <MobileBar
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="center"
        >
          <Button variant="contained" fullWidth onClick={openOrder}>
            <Typography variant="body1" style={{ marginRight: 8 }}>
              View Order
            </Typography>
            <MobileBadge badgeContent={totalSelectedItems} color="secondary">
              <ShoppingCartIcon fontSize="small" />
            </MobileBadge>
          </Button>
          <InfoToggleGroup size="medium">
            <ToggleButton value="info" onClick={() => setDialOpen(!dialOpen)}>
              <InfoIcon />
            </ToggleButton>
          </InfoToggleGroup>
          <Dial
            ariaLabel="Options"
            onClose={() => setDialOpen(false)}
            onOpen={() => setDialOpen(true)}
            open={dialOpen}
            direction="up"
            hidden
          >
            {dialActions.map((action) => (
              <SpeedDialAction
                key={action.key}
                icon={action.icon}
                onClick={() => {
                  action.onClick?.();
                  setDialOpen(false);
                }}
                sx={{ whiteSpace: "nowrap" }}
                // The original passes these through MUI v6's `slotProps`
                // (`{ tooltip: { open: true, title }, fab: { size: "medium" } }`).
                // This project is on MUI v5, where the same three settings are
                // `tooltipTitle` / `tooltipOpen` / `FabProps` — the rendered
                // result is identical.
                tooltipTitle={action.label}
                tooltipOpen
                FabProps={{ size: "medium" }}
              />
            ))}
          </Dial>
        </MobileBar>
      ) : isModernDesign ? (
        <ViewCartPill
          totalSelectedItems={totalSelectedItems}
          totalPrice={totals.total}
          totalSavings={totalSavings}
          currencySymbol={settings.currency_symbol}
          currencyCode={settings.currency_code}
          orderBarOpened={orderBarOpened}
          // Opening routes staff to checkout; closing still just closes.
          setOpen={(next) => (next ? openOrder() : setOpen(false))}
        />
      ) : null}
    </Fragment>
  );
}
